/**
 * `/liturgies` — the dashboard.
 *
 * Measurements from the design canvas (`Pius Selections.dc.html`). The eyebrow reads **"Authorized
 * collection · not a search"** and the footer says so again in full, because the distinction is
 * load-bearing rather than decorative: Templates are browsable, Liturgies are never generally
 * filter widens it.
 *
 * Five columns — Liturgy · Celebrated · Place · Lifecycle · Updated — over three collections: mine,
 * shared, archived (docs/engineering/11-screens.md).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { LifecycleState, TemplateVisibility } from '@pius/contracts';

import { EmptyState } from '../../components/primitives';
import { useAuth } from '../auth/useAuth';
import { destinationOf, fetchInbox, markRead, type InboxEntry } from '../notifications/api';
import { fetchAffiliations } from '../organizations/api';
import { fetchTemplates, type TemplateSummary } from '../templates/api';
import {
  fetchLiturgies,
  fetchReadiness,
  renounceAccess,
  type LiturgySummary,
  type ReadinessItem,
} from './api';
import './liturgies.css';

export type Collection = 'mine' | 'shared' | 'archived';

/**
 * The three collections, each its own route so a tab is deep-linkable and survives a reload.
 *
 * They are tabs over one already-authorized set, not filters that fetch different things: the
 * server decided what this Account may see before any of this ran, and choosing a tab can only ever
 * show less of that set, never more.
 */
const COLLECTIONS = [
  { key: 'mine', label: 'Mine', to: '/liturgies' },
  { key: 'shared', label: 'Shared with me', to: '/liturgies/shared' },
  { key: 'archived', label: 'Archived', to: '/liturgies/archived' },
] as const satisfies ReadonlyArray<{ key: Collection; label: string; to: string }>;

/**
 * The one collection a Liturgy belongs to — exactly one, always.
 *
 * A Liturgy that was shared with this Account and then archived answers to two descriptions at
 * once, so something has to break the tie. **Archived wins**, because the two facts are not the
 * same kind of fact: `archived_at` is a lifecycle state of the Liturgy itself, while `access_via`
 * describes only this Account's route to it. Shared therefore means live shared work, which is what
 * somebody opening that tab is looking for.
 *
 * Deciding membership in one place, returning one collection, is what stops a row appearing under
 * two tabs or — the worse failure, because nobody goes looking for it — under neither.
 *
 * `access_via` places a row. It is never consulted to decide what may be done with it; that
 * authority lives server-side in PlanningAccess (docs/engineering/04-authorization.md).
 */
export function collectionOf(liturgy: LiturgySummary): Collection {
  if (liturgy.archived_at !== null) return 'archived';
  return liturgy.access_via === 'delegation' ? 'shared' : 'mine';
}

const LIFECYCLE_TONE: Record<LifecycleState, string> = {
  draft: 'lit-pill--neutral',
  planning: 'lit-pill--active',
  ready_for_finalization: 'lit-pill--ready',
  finalized: 'lit-pill--active',
  archived: 'lit-pill--neutral',
};

const LIFECYCLE_KEY: Record<LifecycleState, string> = {
  draft: 'lifecycle.draft',
  planning: 'lifecycle.planning',
  ready_for_finalization: 'lifecycle.ready_for_finalization',
  finalized: 'lifecycle.finalized',
  archived: 'lifecycle.archived',
};

export function DashboardScreen({ collection }: { collection: Collection }): React.JSX.Element {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const liturgies = useQuery({ queryKey: ['liturgies'], queryFn: fetchLiturgies });
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const rows = (liturgies.data?.data ?? []).filter((l) => collectionOf(l) === collection);

  return (
    <div className="lit-dash">
      <header className="lit-dash__head">
        <div>
          <p className="lit-eyebrow">{t('dashboard.eyebrow')}</p>
          <h1 className="lit-dash__title">{t('dashboard.title')}</h1>
        </div>

        <div className="lit-dash__acts">
          {auth.account?.is_site_administrator ? (
            <Link className="lit-btn lit-btn--quiet" to="/site">
              {t('nav.site')}
            </Link>
          ) : null}
          <Link className="lit-btn lit-btn--quiet" to="/organizations">
            {t('nav.organizations')}
          </Link>
          <Link className="lit-btn lit-btn--primary" to="/liturgies/new">
            {t('nav.new_liturgy')}
          </Link>
        </div>
      </header>

      {/*
        The body grid: `1fr 292px`, from the canvas. Two cards in the main column, three panels in
        the aside — which is *not* what `11-screens.md:131` says, and the canvas is authority #1.

        292px rather than `--aside-width: 320px` deliberately. That token is the Overview's aside;
        the two screens genuinely differ and unifying them would encode a symmetry the design does
        not have.
      */}
      <div className="lit-dash__body">
        <div className="lit-dash__main">
          <div className="lit-panel">
            <div className="lit-panel__filters">
              <nav className="lit-panel__chips" aria-label="Collection">
                {COLLECTIONS.map((c) => (
                  <Link
                    key={c.key}
                    to={c.to}
                    className={`lit-filter${collection === c.key ? ' lit-filter--active' : ''}`}
                    {...(collection === c.key ? { 'aria-current': 'page' as const } : {})}
                  >
                    {t(`dashboard.collections.${c.key}`)}
                  </Link>
                ))}
              </nav>

              {/* The live route, shown because each collection genuinely is one — see COLLECTIONS. */}
              <span className="lit-panel__route">{pathname}</span>
            </div>

            {/*
            A real table, not grid divs. The canvas draws divs because it is a static mockup; here
            the header cells must be associated with the data cells, so a screen reader announces
            "Lifecycle: Planning" rather than an unlabelled word.
          */}
            <table className="lit-table">
              <thead>
                <tr>
                  <th scope="col" className="lit-table__c-liturgy">
                    {t('dashboard.col_liturgy')}
                  </th>
                  <th scope="col" className="lit-table__c-when">
                    {t('dashboard.col_when')}
                  </th>
                  <th scope="col" className="lit-table__c-place">
                    {t('dashboard.col_place')}
                  </th>
                  <th scope="col" className="lit-table__c-state">
                    {t('dashboard.col_state')}
                  </th>
                  <th scope="col" className="lit-table__c-updated">
                    {t('dashboard.col_updated')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {liturgies.isPending ? (
                  <tr>
                    <td className="lit-table__state" colSpan={5}>
                      {t('dashboard.loading')}
                    </td>
                  </tr>
                ) : liturgies.isError ? (
                  <tr>
                    <td className="lit-table__state" colSpan={5}>
                      {t('dashboard.error')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="lit-table__empty" colSpan={5}>
                      <CollectionEmptyState collection={collection} />
                    </td>
                  </tr>
                ) : (
                  rows.map((liturgy) => <LiturgyRow key={liturgy.id} liturgy={liturgy} />)
                )}
              </tbody>
            </table>

            <p className="lit-panel__footnote">{t('dashboard.footnote')}</p>
          </div>

          <MyTemplatesCard />
        </div>

        <aside className="lit-dash__aside" aria-label={t('dashboard.aside_label')}>
          <InvitationsPanel />
          <NeedsAttentionPanel />
          <AffiliationsPanel />
        </aside>
      </div>
    </div>
  );
}

/**
 * "My templates" — the second card in the **main** column, not an aside panel.
 *
 * `GET /templates` returns everything the viewer may *see*, public templates included, and this card
 * claims to list what they *own*. Those are different questions, so it filters on `is_owner` — a
 * fact about the Resource — rather than on `can_edit`, which is an authorization outcome that today
 * coincides with ownership and is free to stop coinciding. Filtering on the coincidence would work
 * until the day it silently listed other people's work under a heading that says it is yours.
 */
function MyTemplatesCard(): React.JSX.Element {
  const { t } = useTranslation('common');
  const { account } = useAuth();

  // Ownership needs an Account, so an anonymous viewer owns nothing and there is nothing to ask.
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: fetchTemplates,
    enabled: account !== null,
  });

  const mine = (templates.data ?? []).filter((template) => template.is_owner);

  const empty = (
    <EmptyState
      heading={t('dashboard.templates.empty_heading')}
      body={t('dashboard.templates.empty_body')}
      {...(account
        ? {
            action: (
              <Link className="lit-btn lit-btn--secondary" to="/templates/new">
                {t('dashboard.templates.empty_action')}
              </Link>
            ),
          }
        : {})}
    />
  );

  return (
    <div className="lit-panel">
      <div className="lit-tplcard__head">
        <p className="lit-eyebrow">{t('dashboard.templates.eyebrow')}</p>
        <span className="lit-tplcard__note">{t('dashboard.templates.note')}</span>

        {/*
          A fixed chip, unlike the liturgy table's, because there is no `/templates/mine` route for
          it to report. The canvas draws `/templates/mine`; naming a route that does not resolve
          would be the one thing a route chip must never do, so it names the one these came from.
        */}
        <span className="lit-panel__route">/templates</span>
      </div>

      <table className="lit-tpltable">
        <thead>
          <tr>
            <th scope="col" className="lit-tpltable__c-name">
              {t('dashboard.templates.col_template')}
            </th>
            <th scope="col" className="lit-tpltable__c-origin">
              {t('dashboard.templates.col_origin')}
            </th>
            <th scope="col" className="lit-tpltable__c-visibility">
              {t('dashboard.templates.col_visibility')}
            </th>
            <th scope="col" className="lit-tpltable__c-version">
              {t('dashboard.templates.col_version')}
            </th>
          </tr>
        </thead>

        <tbody>
          {account && templates.isPending ? (
            <tr>
              <td className="lit-table__state" colSpan={4}>
                {t('state.loading')}
              </td>
            </tr>
          ) : templates.isError ? (
            <tr>
              <td className="lit-table__state" colSpan={4}>
                {t('dashboard.templates.error')}
              </td>
            </tr>
          ) : mine.length === 0 ? (
            <tr>
              <td className="lit-table__empty" colSpan={4}>
                {empty}
              </td>
            </tr>
          ) : (
            mine.map((template) => <TemplateRow key={template.id} template={template} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

const VISIBILITY_LABEL: Record<TemplateVisibility, string> = {
  private: 'dashboard.templates.visibility_private',
  organization: 'dashboard.templates.visibility_organization',
  public: 'dashboard.templates.visibility_public',
};

function TemplateRow({ template }: { template: TemplateSummary }): React.JSX.Element {
  const { t } = useTranslation('common');

  // Outermost first: `lineage[0]` is this template, the rest is what it specializes.
  const origin =
    template.lineage.length > 1
      ? t('dashboard.templates.origin_specializes', {
          lineage: template.lineage.slice(1).join(' ← '),
        })
      : t('dashboard.templates.origin_original');

  return (
    <tr>
      <td className="lit-tpltable__c-name">
        <Link className="lit-tplrow__name" to="/templates/$id/edit" params={{ id: template.id }}>
          {template.name}
        </Link>

        {/*
          The canvas puts a section count here. `TemplateSummary` does not carry one, and getting it
          would cost a query per row — the exact N+1 the capacity dataset caught in this listing and
          `templates.controller.ts:70-74` records being fixed. The slot carries a fact the DTO does
          have instead, and stays empty when there is none, rather than showing invented data.
        */}
        {template.has_draft ? (
          <div className="lit-tplrow__sub">{t('dashboard.templates.draft_in_progress')}</div>
        ) : null}
      </td>

      <td className="lit-tpltable__c-origin lit-tplrow__origin">{origin}</td>

      <td className="lit-tpltable__c-visibility">
        <div className="lit-tplrow__badges">
          <span className={`lit-tplrow__badge lit-tplrow__badge--${template.visibility}`}>
            {t(VISIBILITY_LABEL[template.visibility])}
          </span>

          {template.retired_at ? (
            <span className="lit-tplrow__badge lit-tplrow__badge--retired">
              {t('dashboard.templates.retired')}
            </span>
          ) : null}

          {/* Designation changes discoverability, never authority — and never ownership. */}
          {template.designations.map((designation) => (
            <span
              key={designation}
              className="lit-tplrow__badge lit-tplrow__badge--designation"
              // Server vocabulary the site administrator set; shown as it came rather than mapped
              // to a key that may not exist.
            >
              {designation}
            </span>
          ))}
        </div>
      </td>

      <td className="lit-tpltable__c-version lit-tplrow__version">
        {template.latest_published_version === null
          ? t('dashboard.templates.unpublished')
          : t('dashboard.templates.version', { number: template.latest_published_version })}
      </td>
    </tr>
  );
}

/**
 * Invitations — the unread `collaborator.added` notifications, and nothing else.
 *
 * There is no acceptance step in this product and this panel does not invent one. Sharing writes
 * the delegation immediately (v0.2 Collaboration Model §5: *"Invitation delivery does not itself
 * grant authority beyond that represented by the associated Capability or Account relationship"*),
 * so by the time these rows render the access already exists.
 *
 * That decides both buttons. **Open** navigates and marks the notification read; it grants nothing,
 * because there is nothing left to grant, and it is not labelled "Accept" for that reason. **Give
 * back access** revokes the reader's own delegation — the only act here that changes anything, and
 * one the reader cannot undo, since nobody can grant themselves access. It is confirmed first, and
 * the confirmation says what actually happens rather than asking "are you sure?".
 */
function InvitationsPanel(): React.JSX.Element {
  const { t } = useTranslation('common');
  const { account } = useAuth();

  const inbox = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchInbox,
    enabled: account !== null,
  });

  const invitations = (inbox.data?.data ?? []).filter(
    (entry) => entry.kind === 'collaborator.added' && !entry.read && entry.liturgy_id !== null,
  );

  return (
    <section className="lit-aside-card">
      <p className="lit-eyebrow">{t('dashboard.invitations.eyebrow')}</p>

      {account && inbox.isPending ? (
        <p className="lit-aside-card__state">{t('state.loading')}</p>
      ) : inbox.isError ? (
        <p className="lit-aside-card__state">{t('dashboard.invitations.error')}</p>
      ) : invitations.length === 0 ? (
        <EmptyState
          compact
          heading={t('dashboard.invitations.empty_heading')}
          body={t('dashboard.invitations.empty_body')}
        />
      ) : (
        <ul className="lit-aside-card__list lit-invites">
          {invitations.map((entry) => (
            <InvitationItem key={entry.id} entry={entry} />
          ))}
        </ul>
      )}
    </section>
  );
}

function InvitationItem({ entry }: { entry: InboxEntry }): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  // Derived here rather than taken from `payload.link_path`, which is a value the server composed
  // for an email — see `destinationOf`. A payload field must not be able to steer navigation.
  const destination = destinationOf(entry);

  const read = useMutation({
    mutationFn: () => markRead(entry.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const decline = useMutation({
    mutationFn: async () => {
      if (!entry.liturgy_id) return;
      await renounceAccess(entry.liturgy_id);
      // Only after the access is actually given back. Marking read first would leave the row gone
      // and the delegation standing if the revoke failed.
      await markRead(entry.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['liturgies'] });
    },
  });

  return (
    <li className="lit-invite">
      <p className="lit-invite__title">
        {entry.liturgy_title ?? t('dashboard.invitations.untitled')}
      </p>
      <p className="lit-invite__detail">
        {t('dashboard.invitations.detail', { date: formatUpdated(entry.created_at) })}
      </p>

      {confirming ? (
        <>
          <p className="lit-invite__warning">{t('dashboard.invitations.confirm_body')}</p>
          <div className="lit-invite__acts">
            <button
              type="button"
              className="lit-btn lit-btn--danger"
              disabled={decline.isPending}
              onClick={() => decline.mutate()}
            >
              {t('dashboard.invitations.confirm_action')}
            </button>
            <button
              type="button"
              className="lit-btn lit-btn--quiet"
              onClick={() => setConfirming(false)}
            >
              {t('dashboard.invitations.confirm_cancel')}
            </button>
          </div>
        </>
      ) : (
        <div className="lit-invite__acts">
          {destination ? (
            <Link
              className="lit-btn lit-btn--accept"
              to={destination}
              onClick={() => read.mutate()}
            >
              {t('dashboard.invitations.open')}
            </Link>
          ) : null}
          <button
            type="button"
            className="lit-btn lit-btn--quiet"
            onClick={() => setConfirming(true)}
          >
            {t('dashboard.invitations.decline')}
          </button>
        </div>
      )}

      {decline.isError ? (
        <p className="lit-invite__error">{t('dashboard.invitations.error')}</p>
      ) : null}
    </li>
  );
}

/**
 * Needs attention — unmet Finalization conditions, and deliberately nothing else.
 *
 * `GET /liturgies/readiness` returns only unapproved constraint Items and unfilled constraint
 * Roles, so every row here blocks a Finalization. It is the Overview's "Before this Liturgy can be
 * Finalized" card across the whole collection, and it must stay that: the moment it grows a general
 * activity feed, the one signal that means "this cannot be finalized" is lost in it.
 *
 * The route answers `{ data: [] }` for an anonymous caller, so the empty state doubles as the
 * anonymous state with no special case.
 */
function NeedsAttentionPanel(): React.JSX.Element {
  const { t } = useTranslation('common');
  const readiness = useQuery({ queryKey: ['liturgies', 'readiness'], queryFn: fetchReadiness });

  const rows = (readiness.data?.data ?? []).flatMap(toAttentionRows);
  const shown = rows.slice(0, ATTENTION_LIMIT);
  const remaining = rows.length - shown.length;

  return (
    <section className="lit-aside-card">
      <p className="lit-eyebrow">{t('dashboard.attention.eyebrow')}</p>

      {readiness.isPending ? (
        <p className="lit-aside-card__state">{t('state.loading')}</p>
      ) : readiness.isError ? (
        <p className="lit-aside-card__state">{t('dashboard.attention.error')}</p>
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          heading={t('dashboard.attention.empty_heading')}
          body={t('dashboard.attention.empty_body')}
        />
      ) : (
        <>
          <ul className="lit-aside-card__list lit-attention">
            {shown.map((row) => (
              <li key={row.key}>
                <Link
                  className="lit-attention__link"
                  to="/liturgies/$id"
                  params={{ id: row.liturgyId }}
                >
                  <span className="lit-attention__dot" aria-hidden="true" />
                  <span className="lit-attention__body">
                    <span className="lit-attention__text">
                      {t(row.textKey, { label: row.label })}
                    </span>
                    <span className="lit-attention__where">{row.where}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {remaining > 0 ? (
            <p className="lit-aside-card__state">
              {t('dashboard.attention.more', { count: remaining })}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * How many rows the aside shows before it summarises the rest.
 *
 * A panel is a signal, and an unbounded one stops being read. The remainder is stated as a count
 * rather than hidden — the reader is told the list is longer than this.
 */
const ATTENTION_LIMIT = 8;

interface AttentionRow {
  key: string;
  liturgyId: string;
  textKey: string;
  label: string;
  where: string;
}

/**
 * One readiness record becomes one row per unmet condition.
 *
 * Both kinds are Constraints — an Item with no approved revision, a constraint Role with no
 * confirmed assignment — so both are rendered identically. Distinguishing them by colour would
 * assert a severity ordering the model does not define: either blocks Finalization outright.
 */
function toAttentionRows(item: ReadinessItem): AttentionRow[] {
  return [
    ...item.unsatisfied_items.map((unsatisfied) => ({
      key: `item:${unsatisfied.id}`,
      liturgyId: item.liturgy_id,
      textKey: 'dashboard.attention.unsatisfied_item',
      label: unsatisfied.label,
      where: item.title,
    })),
    ...item.unassigned_roles.map((role) => ({
      key: `role:${item.liturgy_id}:${role.role_key}`,
      liturgyId: item.liturgy_id,
      textKey: 'dashboard.attention.unassigned_role',
      label: role.label,
      where: item.title,
    })),
  ];
}

/**
 * Affiliations — context, and nothing that can be acted on.
 *
 * No links, no counts, no "manage" affordance, by requirement: Organization UI must never imply
 * authority (L3 UI Component Architecture §33), and this card is where that is easiest to break.
 * Affiliation records where somebody serves and supplies Defaults; a Default is an overridable
 * starting point, never a Constraint. The closing sentence is verbatim from the working spec.
 */
function AffiliationsPanel(): React.JSX.Element {
  const { t } = useTranslation('common');
  const { account } = useAuth();

  // Affiliation is a relationship an Account holds. Anonymous participation is first-class, and it
  // simply has none — so there is nothing to ask the server.
  const affiliations = useQuery({
    queryKey: ['affiliations'],
    queryFn: fetchAffiliations,
    enabled: account !== null,
  });

  const organizations = affiliations.data?.data ?? [];

  return (
    <section className="lit-aside-card lit-aside-card--warm">
      <p className="lit-eyebrow">{t('dashboard.affiliations.eyebrow')}</p>

      {account && affiliations.isPending ? (
        <p className="lit-aside-card__state">{t('state.loading')}</p>
      ) : affiliations.isError ? (
        <p className="lit-aside-card__state">{t('dashboard.affiliations.error')}</p>
      ) : organizations.length === 0 ? (
        <EmptyState
          compact
          heading={t('dashboard.affiliations.empty_heading')}
          body={t('dashboard.affiliations.empty_body')}
        />
      ) : (
        <ul className="lit-aside-card__list">
          {organizations.map((organization) => (
            <li key={organization.id} className="lit-affiliation">
              {organization.name}
            </li>
          ))}
        </ul>
      )}

      <p className="lit-affiliations__note">{t('dashboard.affiliations.note')}</p>
    </section>
  );
}

/**
 * Nothing here.
 *
 * One sentence per collection and never a reason — an empty Shared collection reads identically
 * whether nothing was ever shared or everything was revoked. See EmptyState.
 */
function CollectionEmptyState({ collection }: { collection: Collection }): React.JSX.Element {
  const { t } = useTranslation('common');

  if (collection === 'mine') {
    return (
      <EmptyState
        illustration="/brand/shield.png"
        heading={t('empty.liturgies_mine.heading')}
        body={t('empty.liturgies_mine.body')}
        action={
          <Link className="lit-btn lit-btn--secondary" to="/liturgies/new">
            {t('empty.liturgies_mine.action')}
          </Link>
        }
      />
    );
  }

  const key = collection === 'shared' ? 'liturgies_shared' : 'liturgies_archived';

  return (
    <EmptyState
      illustration="/brand/shield.png"
      heading={t(`empty.${key}.heading`)}
      body={t(`empty.${key}.body`)}
    />
  );
}

function LiturgyRow({ liturgy }: { liturgy: LiturgySummary }): React.JSX.Element {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const celebrated = formatCelebration(
    liturgy.celebration_local_datetime,
    liturgy.celebration_timezone,
  );

  /**
   * The whole row navigates, but the title is a real link — that is what a keyboard reaches and
   * what a screen reader announces. The row handler is the mouse convenience on top of it, and it
   * stands down when the link has already handled the click rather than navigating twice.
   */
  const open = (event: React.MouseEvent<HTMLTableRowElement>): void => {
    if (event.defaultPrevented) return;
    void navigate({ to: '/liturgies/$id', params: { id: liturgy.id } });
  };

  return (
    <tr className="lit-table__row" onClick={open}>
      <td className="lit-table__c-liturgy">
        <Link
          className="lit-row__title"
          to="/liturgies/$id"
          params={{ id: liturgy.id }}
          aria-label={liturgy.title}
        >
          {liturgy.title}
        </Link>
      </td>

      <td className="lit-table__c-when lit-row__when">
        {celebrated ? (
          <>
            <div>{celebrated.date}</div>
            <div className="lit-row__time">{celebrated.time}</div>
          </>
        ) : (
          <span className="lit-row__muted">{t('dashboard.not_scheduled')}</span>
        )}
      </td>

      <td className="lit-table__c-place lit-row__place">
        {liturgy.place ?? <span className="lit-row__muted">—</span>}
      </td>

      <td className="lit-table__c-state">
        <span className={`lit-pill ${LIFECYCLE_TONE[liturgy.lifecycle_state]}`}>
          {t(LIFECYCLE_KEY[liturgy.lifecycle_state])}
        </span>
      </td>

      <td className="lit-table__c-updated lit-row__updated">{formatUpdated(liturgy.updated_at)}</td>
    </tr>
  );
}

/**
 * `updated_at` is a real instant, unlike a celebration's wall-clock time, so it is rendered in the
 * reader's own zone: "when did this change" is a question about the reader's day.
 */
function formatUpdated(updatedAt: string): string {
  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * A celebration's time is **local wall-clock time in the parish's own zone**, not an instant. It is
 * formatted in that zone rather than the viewer's: a 10:00 Mass is at 10:00 for the parish planning
 * it, whoever happens to be looking (docs/engineering/01-domain-model.md).
 */
function formatCelebration(
  localDateTime: string | null,
  timezone: string | null,
): { date: string; time: string } | null {
  if (!localDateTime) return null;

  const parsed = new Date(localDateTime);
  if (Number.isNaN(parsed.getTime())) return null;

  // The stored value is wall-clock, so it is rendered in UTC — which is how it was stored — rather
  // than shifted into the viewer's zone, which would move the time.
  const date = parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const time = parsed.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });

  return { date, time: timezone ? `${time} · ${timezone}` : time };
}
